import * as React from 'react';

import { useDispatch } from 'react-redux';

import {
	Label,
	IBoxComponentProps,
	Text,
	Box,
	Image,
	Pagination,
	PaginationButton,
	Modal,
	ModalOverlay,
	ModalCloseButton,
	ModalHeader,
	ModalBody,
	ModalContent,
	Tags,
	Tag,
	TagInput,
	Button,
	ActionsDropdown,
	ActionsDropdownArrow,
	ActionsDropdownItem,
	IconButton,
	IInputCallbackData,
	Checkbox,
	CheckboxIcon,
	Description,
	TagText,
	TagClose,
} from '@moosend/mooskin';
import { PermissionsCheck } from './PermissionsAndLimitsContext';
import { AccountsServiceModels } from 'accounts/exports';
import { Link } from 'react-router-dom';
import { NotificationSlice } from 'notification/exports';
import { translateText } from '../../util/translationHelper';
import { getAITagTransformed, isAITag } from '../../reactCommons/helpers';

const { actions: NotificationActions } = NotificationSlice;

export const TableContainerHeader: React.FC<{ image: string; title: string } & IBoxComponentProps> = (props) => {
	return (
		<Box boxShadow="lg" bgColor="backgroundColors.white" noRender={['xs', 'sm']}>
			<Box d="flex" flexWrap="wrap" align="center" h={87} px={[30, 30, 10, 10]} m="0 auto" maxW={1500}>
				<Box d="flex" align="center" flex={1}>
					<Image src={props.image} w={60} />
					<Label ml={20} fontSize={29}>
						{props.title}
					</Label>
				</Box>
				<Box d="flex">{props.children}</Box>
			</Box>
		</Box>
	);
};

export const TableContainerWrapper: React.FC<IBoxComponentProps> = (props) => {
	return (
		<Box p={[30, 30, 10, 10]} pb={130} maxW={1500} m="0 auto" {...props}>
			{props.children}
		</Box>
	);
};

export const TableContainerPagination: React.FC<
	{
		onClick: (e: React.MouseEvent<HTMLElement>, page: number) => void;
		totalPages: number;
		currentPage?: number;
	} & IBoxComponentProps
> = (props) => {
	if (typeof props.currentPage !== 'undefined') {
		return (
			<Pagination py={20} flexWrap="wrap" activePage={props.currentPage} onClickButton={props.onClick} noRender={['sm', 'xs']}>
				{[...Array(props.totalPages)].map((item, i) => {
					return <PaginationButton my={10} key={i} />;
				})}
			</Pagination>
		);
	}
	return null;
};

export interface ITableLAbelsProps {
	itemId: string;
	currentTags: string[];
	permissions: AccountsServiceModels.PermissionsEnum[];
	activeTags: string[];
	allTags: string[];
	allSystemTags?: string[];
	refName?: { singular: string; plural: string };
	fixedDropdown?: boolean;
	onTagsSave: (tags: string[], itemId: string) => void;
	onTagsRemove: (data: { index: number; itemId: string }) => void;
	onTagClick: (tag: string) => void;
}

const getTagText = (item: string) => (isAITag(item) ? getAITagTransformed(item).label : item);

export const TableLabels: React.FC<ITableLAbelsProps> = (props) => {
	const [tagsModal, setTagsModal] = React.useState(false);
	const [tagsDropdown, setTagsDropdown] = React.useState(false);
	const [editingTags, setEditingTags] = React.useState(props.currentTags);
	const [inputValue, setInputValue] = React.useState('');

	const { refName, fixedDropdown } = props;
	const dropdownRef = React.useRef<HTMLDivElement>(null);

	const dispatch = useDispatch();

	React.useEffect(() => {
		if (!tagsModal) {
			setInputValue('');
		}
	}, [tagsModal]);

	const onTagAdd = (tag: string) => {
		if (!tag.trim())
			return dispatch(
				NotificationActions.showNotification({
					message: `${refName?.singular ?? 'Label'} should not be empty!`,
					type: 'error',
				})
			);
		if (tag.length < 3) {
			return dispatch(
				NotificationActions.showNotification({
					message: `${refName?.plural ?? 'Labels'} must consist of 3 to 20 characters.`,
					type: 'error',
				})
			);
		}
		if (editingTags.includes(tag)) {
			return dispatch(
				NotificationActions.showNotification({
					message: `${refName?.singular ?? 'Label'} already exists!`,
					type: 'error',
				})
			);
		}
		setEditingTags([...editingTags, tag]);
	};

	const onTagsSave = () => {
		let tags = [...editingTags];
		if (inputValue) {
			const valueToCheck = inputValue;
			const isSystemTag = props.allSystemTags?.includes(valueToCheck);

			if (!valueToCheck) return;

			if (isSystemTag) {
				dispatch(
					NotificationActions.showNotification({
						message: 'Cannot save system labels',
						type: 'error',
					})
				);
				return;
			}

			tags = [...tags, inputValue];
		}

		if (tags.filter((item) => !isAITag(item)).length > 6) {
			dispatch(
				NotificationActions.showNotification({
					message: `Can't have more than 6 ${refName?.plural.toLowerCase() ?? 'labels'}!`,
					type: 'error',
				})
			);
			return;
		}

		tags = tags.filter((item) => item !== 'Transactional');
		props.onTagsSave && props.onTagsSave(tags, props.itemId);
		onClose();
	};

	const onTagsRemove = (e: React.SyntheticEvent, data: IInputCallbackData) => {
		props.onTagsRemove && props.onTagsRemove({ index: data.index, itemId: props.itemId });
	};

	const onEditTagsRemove = (e: React.SyntheticEvent, data: IInputCallbackData) => {
		const tags = [...editingTags];
		tags.splice(data.value, 1);
		setEditingTags(tags);
	};

	const onTagClick = (tag: string) => {
		props.onTagClick && props.onTagClick(tag);
	};

	const onClose = () => {
		setTagsModal(false);
		setEditingTags(props.currentTags);
	};

	const onOpen = () => {
		setTagsModal(true);
		setEditingTags(props.currentTags);
	};

	const onAddTagManually = (tag?: string) => {
		if (editingTags.filter((item) => !isAITag(item)).length >= 6) {
			dispatch(
				NotificationActions.showNotification({
					message: `Can't have more than 6 ${refName?.plural.toLowerCase() ?? 'labels'}!`,
					type: 'error',
				})
			);
		} else {
			const valueToCheck = tag || inputValue;
			const isSystemTag = props.allSystemTags?.includes(valueToCheck);

			if (!valueToCheck) return;

			if (!isSystemTag) {
				onTagAdd(valueToCheck);
			} else {
				dispatch(
					NotificationActions.showNotification({
						message: 'Cannot add system labels',
						type: 'error',
					})
				);
			}
		}
		setInputValue('');
	};

	const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.keyCode === 13) {
			onAddTagManually();
		} else if (!inputValue && e.keyCode === 8 && editingTags.length) {
			onEditTagsRemove({} as any, { dataLabel: '', value: editingTags.length - 1 });
		}
	};

	const renderSuggestions = () => {
		const filteredLabels = props.allSystemTags ? props.allTags.filter((tag) => !(props.allSystemTags ?? []).includes(tag)) : props.allTags;
		const suggestions = inputValue
			? filteredLabels.filter((item) => {
					return item.includes(inputValue);
			  })
			: filteredLabels;

		if (!suggestions.length) {
			return null;
		}

		return (
			<Box borderRadius={3} border="solid 2px #9d9d9d" mb={10} w="100%" maxH={178} overflow="auto">
				{suggestions.map((item, i) => {
					return props.allSystemTags && props.allSystemTags.includes(item) ? null : (
						<Text cursor="pointer" fontColor="fontColors.medgray1" px={10} py={5} key={i} onClick={() => onAddTagManually(item)}>
							{item}
						</Text>
					);
				})}
			</Box>
		);
	};

	const renderTagsModal = () => {
		return (
			<Modal isOpen={tagsModal} round="sm" onClose={onClose}>
				<ModalOverlay>
					<ModalContent maxW={550} w="100%" h="fit-content" borderRadius={5}>
						<ModalCloseButton position="absolute" fontSize={18} top={24} right={24} w={18} h={18} />
						<ModalHeader p={23} borderTopLeftRadius={5} borderTopRightRadius={5}>
							<Label fontSize={16}>{translateText(`Add ${refName?.plural.toLowerCase() ?? 'labels'}`)}</Label>
						</ModalHeader>
						<ModalBody position="relative" p={0}>
							<Box p={20}>
								{/* {props.allTags && props.allTags.length ? (
									<Select onChangeSelect={(e, data) => onTagAdd(data.value)}>
										<SelectContainer>
											<SelectPlaceholder>Add existing labels</SelectPlaceholder>
										</SelectContainer>
										<SelectOptionList>
											{props.allTags.map((tag, i) => {
												return (
													<SelectOption key={i} value={tag}>
														{tag}
													</SelectOption>
												);
											})}
										</SelectOptionList>
									</Select>
								) : null} */}
								<Tags onAddTag={(data) => onTagAdd(data.value)} onRemoveTag={onEditTagsRemove} pb={5} mb={8}>
									<Box
										d="flex"
										position="relative"
										w="100%"
										mb={5}
										borderRadius={3}
										border="solid 2px"
										borderColor="borderColors.primary2"
										p={10}
									>
										<TagInput
											flex="1"
											ml={5}
											value={inputValue}
											autoFocus
											placeholder={translateText(`Add ${refName?.singular.toLowerCase() ?? 'label'}`)}
											maxLength={20}
											onKeyDown={handleOnKeyDown}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
										/>
										<IconButton onClick={() => onAddTagManually()}>add</IconButton>
									</Box>
									{renderSuggestions()}
									<Box w="100%" d="flex" flexWrap="wrap">
										{editingTags.map((item: string, i) => {
											return (
												<Tag
													my={2}
													key={i}
													className="notranslate"
													borderStyle="solid"
													borderWidth="1px"
													borderColor="rgba(41, 51, 70, 0.26)"
													bgColor={isAITag(item) ? getAITagTransformed(item).color : 'backgroundColors.white'}
													fontColor="fontColors.primary2"
													title={getTagText(item)}
													removeIcon={!(isAITag(item) || (props.allSystemTags && props.allSystemTags.includes(item)))}
												>
													<TagText>{getTagText(item)}</TagText>
													{!(isAITag(item) || (props.allSystemTags && props.allSystemTags.includes(item))) ? (
														<TagClose fontColor="fontColors.primary2">highlight_off</TagClose>
													) : null}
												</Tag>
											);
										})}
									</Box>
								</Tags>
								<Button ml="auto" onClick={onTagsSave}>
									Save {refName?.plural.toLowerCase() ?? 'labels'}
								</Button>
							</Box>
						</ModalBody>
					</ModalContent>
				</ModalOverlay>
			</Modal>
		);
	};

	const renderDropdown = (tags: string[]) => {
		return (
			<ActionsDropdown
				isOpen={tagsDropdown}
				boxShadow="md"
				position={fixedDropdown ? 'fixed' : 'absolute'}
				top={fixedDropdown && dropdownRef.current ? dropdownRef.current.getBoundingClientRect().top + 27 : 27}
				right={fixedDropdown ? undefined : -13}
				left={fixedDropdown && dropdownRef.current ? dropdownRef.current.getBoundingClientRect().left - 54 : undefined}
				bgColor={props.activeTags.some((item) => tags.includes(item)) ? 'backgroundColors.primary1' : 'backgroundColors.white'}
				maxH={130}
				overflow="auto"
			>
				<ActionsDropdownArrow
					bgColor={props.activeTags.some((item) => tags.includes(item)) ? 'backgroundColors.primary1' : 'backgroundColors.white'}
				/>
				{tags.map((tag, i) => {
					return (
						<ActionsDropdownItem
							key={i}
							onClick={() => onTagClick(tag)}
							d="flex"
							justify="space-between"
							fontColor={props.activeTags.some((item) => tags.includes(item)) ? 'fontColors.white' : 'fontColors.primary1'}
						>
							{isAITag(tag) ? getAITagTransformed(tag).label : tag}
							{props.activeTags.includes(tag) && (
								<IconButton
									fontSize={12}
									ml={15}
									fontColor={props.activeTags.some((item) => tags.includes(item)) ? 'fontColors.white' : 'fontColors.primary1'}
								>
									check
								</IconButton>
							)}
						</ActionsDropdownItem>
					);
				})}
			</ActionsDropdown>
		);
	};

	const renderTags = () => {
		return (
			<PermissionsCheck requiredPermissions={props.permissions} mode="always-render">
				{(context: { permissions: AccountsServiceModels.PermissionsEnum[] }, pass: boolean) => {
					const extraTags: string[] = [];
					return (
						<>
							<Tags onRemoveTag={pass ? onTagsRemove : undefined}>
								{props.currentTags.map((item: string, i) => {
									if (props.currentTags.length <= 3 || (props.currentTags.length > 3 && i < 2)) {
										return (
											<Tag
												className="notranslate"
												my={2}
												key={i}
												onClick={() => onTagClick(item)}
												bgColor={
													props.activeTags.includes(item)
														? 'backgroundColors.primary2'
														: isAITag(item)
														? getAITagTransformed(item).color
														: 'backgroundColors.white'
												}
												borderStyle="solid"
												borderWidth="1px"
												borderColor="rgba(41, 51, 70, 0.26)"
												fontColor={props.activeTags.includes(item) ? 'fontColors.white' : 'fontColors.primary2'}
												removeIcon={!(isAITag(item) || (props.allSystemTags && props.allSystemTags.includes(item)))}
											>
												<TagText title={getTagText(item)}>{getTagText(item)}</TagText>
												{!(isAITag(item) || (props.allSystemTags && props.allSystemTags.includes(item))) ? (
													<TagClose
														onClick={(e) => {
															e.stopPropagation();
															onTagsRemove(e, { index: i, itemId: props.itemId });
														}}
														fontColor={props.activeTags.includes(item) ? 'fontColors.white' : 'fontColors.primary2'}
													>
														highlight_off
													</TagClose>
												) : null}
											</Tag>
										);
									}
									extraTags.push(item);
								})}
								{extraTags.length > 1 && (
									<div ref={dropdownRef}>
										<Box
											title={`Add ${refName?.singular.toLowerCase() ?? 'label'}`}
											cursor="pointer"
											// bgColor="backgroundColors.primary2"
											bgColor={
												props.activeTags.some((item) => extraTags.includes(item)) ? 'backgroundColors.primary2' : 'backgroundColors.white'
											}
											fontColor={props.activeTags.some((item) => extraTags.includes(item)) ? 'fontColors.white' : 'fontColors.primary2'}
											borderStyle="solid"
											borderWidth="1px"
											borderColor="rgba(41, 51, 70, 0.26)"
											px={11}
											py={1}
											fontSize={12}
											lineHeight={1.7}
											mr={5}
											borderRadius={13}
											position="relative"
											onMouseEnter={() => setTagsDropdown(true)}
											onMouseLeave={() => setTagsDropdown(false)}
										>
											{extraTags.length}
											{renderDropdown(extraTags)}
										</Box>
									</div>
								)}
								{pass && (
									<IconButton
										border="1px solid"
										bgColor="backgroundColors.white"
										fontColor="fontColors.primary2"
										borderColor="rgba(41, 51, 70, 0.26)"
										title={`Add ${refName?.singular.toLowerCase() ?? 'label'}`}
										d="flex"
										align="center"
										justify="center"
										h={22}
										w={22}
										my={2}
										fontSize={12}
										onClick={onOpen}
										children="add"
										borderRadius="50%"
									/>
								)}
							</Tags>
							{renderTagsModal()}
						</>
					);
				}}
			</PermissionsCheck>
		);
	};

	return renderTags();
};

export interface ITableFilterWrapperProps {
	hideWrapper?: boolean;
	hideSwitch?: boolean;
	switchWidth?: number;
	currentTab: string;
	tabOptions: { label: string; route: string; onClick?: () => void }[];
}

export const TableFilterWrapper: React.FC<ITableFilterWrapperProps> = (props) => {
	return (
		<Box
			h={[80, 80, 'auto', 'auto']}
			align="center"
			d={props.hideWrapper ? 'none' : 'flex'}
			borderWidth={[1, 1, '0px', '0px']}
			borderStyle="solid"
			borderColor="borderColors.gray1"
		>
			{!props.hideSwitch && <TableSwitch width={props.switchWidth} title={props.currentTab} options={props.tabOptions} />}
			<Box d="flex" flex={1} justify="flex-end" direction={['row', 'row', 'column', 'column']} px={[15, 15, '0px', '0px']}>
				{props.children}
			</Box>
		</Box>
	);
};

export interface ITableSwitchProps {
	title: string;
	options: { label: string; route: string; onClick?: () => void }[];
	width?: number;
}

export const TableSwitch: React.FC<ITableSwitchProps> = (props) => {
	const [dropdown, setDropdown] = React.useState(false);

	return (
		<Box
			bgColor={dropdown ? 'backgroundColors.white' : ''}
			d="flex"
			align="center"
			h="100%"
			w={props.width || 'auto'}
			// flex={1}
			cursor="pointer"
			boxShadow={dropdown ? 'md' : ''}
			position="relative"
			noRender={['sm', 'xs']}
			onMouseEnter={() => setDropdown(true)}
			onMouseLeave={() => setDropdown(false)}
		>
			<Box mx={15} h="100%" flex={1} d="flex" align="center" justify="space-between" fontColor="fontColors.text">
				<Label fontSize={22} mr={10}>
					{props.title}
				</Label>
				<IconButton>{dropdown ? 'expand_less' : 'expand_more'}</IconButton>
			</Box>
			{dropdown && (
				<Box boxShadow="md" bgColor="backgroundColors.white" position="absolute" top={78} right={0} left={0} zIndex={2}>
					{props.options.map((item) => {
						return (
							<Link key={item.route} to={item.route} onClick={item.onClick}>
								<Text
									py={5}
									px={15}
									// bgColor={!dropdown ? 'transparent' : 'backgroundColors.background'}
									// fontColor={!dropdown ? 'fontColors.text' : 'fontColors.primary1'}
								>
									{item.label}
								</Text>
							</Link>
						);
					})}
				</Box>
			)}
		</Box>
	);
};

interface ITableItemSelectProps {
	dataLabel: string;
	checked: boolean;
	selectItem: (data: IInputCallbackData) => void;
}

export const TableItemSelect: React.FC<ITableItemSelectProps> = (props) => {
	const selectItem = (e: React.MouseEvent, data: IInputCallbackData) => {
		props.selectItem && props.selectItem(data);
	};

	return (
		<Checkbox dataLabel={props.dataLabel} onClickCheckbox={selectItem} checked={props.checked}>
			<CheckboxIcon p={0} fontFamily="Mooskin Icons Outlined" />
		</Checkbox>
	);
};

export const NameWithDetails: React.FC<{ name: string; description?: string }> = ({ name, description }) => {
	return (
		<Box d="flex" direction="column">
			<Text>{name}</Text>
			{description && (
				<Description fontSize={14} mt="0">
					{description}
				</Description>
			)}
		</Box>
	);
};

export const MultiActionsBar: React.FC<{
	actions: { label: string; key: string }[];
	count: number;
	type: string;
	onActionClick: (key: string) => void;
}> = (props) => {
	const { actions, count, onActionClick, type } = props;

	const renderActions = () => {
		return (
			actions.length &&
			actions.map((item, i) => (
				<Button
					key={i}
					onClick={() => onActionClick(item.key)}
					mr={15}
					bgColor="backgroundColors.primary2"
					fontColor="fontColors.white"
					border="1px solid #ffffff"
				>
					{item.label}
				</Button>
			))
		);
	};

	return (
		<Box
			w="100%"
			minH={35}
			position="absolute"
			top={0}
			left={0}
			h="100%"
			d="flex"
			align="center"
			bgColor="backgroundColors.primary2"
			pl={30}
		>
			{!!count && (
				<Box boxAs="p" fontColor="fontColors.white" fontSize={12} mx={20}>
					{count} selected {type}
				</Box>
			)}
			{renderActions()}
		</Box>
	);
};

export const renderSegmentDescription = (description: string) => {
	const transformed = description.replace(/\(/g, '').replace(/\)/g, '');
	return <Box alignSelf="start" fontSize={12} fontWeight="bold" fontColor="fontColors.primary1" children={transformed} />;
};
